import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getVersionAndRelease } from '../../../../../untidy/redux/selectors';
import { formatDateTime } from '../../../../../untidy/texts';
import { getTextStyle } from '../../../../generic/content/texts/bou-text/text-style';
import { getTitleStyle } from '../../../../generic/content/texts/bou-title/title-style';
import { BouColor } from '../../../../generic/global/bou-colors';
import { BouSpacingLevel, fluidSpacing } from '../../../../generic/layout/bou-spacing';
import { bouToolCommonBoxPadding } from '../../../../generic/layout/layout-constants';
import { fluidSpaceLg } from '../../../../generic/utilities/fluid/fluid';
import { zeroWidthNonBreakingSpace } from '../../../../generic/utilities/zero-width-space';
import { useHoldingsSourceDescription } from '../../input/attribution';

/** The legal terms for Upright data. The unified shell shows them in its Help menu's dialog. */
export const DATA_DISCLAIMER_TEXT =
  'The data provided is generated by novel technology that is under constant development. It is provided by Upright ' +
  'and possible 3rd party data contributors "as is", without warranty or guarantee of accuracy of any kind, express ' +
  'or implied, including, but not limited to, the implied warranties of mercantability and fitness for a particular ' +
  'purpose. In no event shall Upright or contributors be liable for any direct, indirect, incidental, special, ' +
  'exemplary, or consequential damages however caused and on any theory of liability, whether in contract, strict ' +
  'liability, or tort (including neglicence or otherwise) arising in any way out of the use of the data, even if ' +
  'advised of the possibility of such damage. This data is not intended to be used as investment advice.';

type Props = {
  entityId?: string;
};

export function ModelReleaseDisclaimer(props: Props) {
  const { release } = useSelector(getVersionAndRelease);
  const [time] = useState(new Date());

  const holdingsSourceDescription = useHoldingsSourceDescription(props.entityId);
  if (!release) {
    return <></>;
  }
  return (
    <Root>
      <TopLine>{zeroWidthNonBreakingSpace}</TopLine>
      <Text>
        Based on Upright
        <br />
        model release
        <br />"{release}"
      </Text>
      <FullDate>{formatDateTime(time)}</FullDate>
      {holdingsSourceDescription && <SourceDisclaimer>{holdingsSourceDescription}</SourceDisclaimer>}
      <DataDisclaimer>{DATA_DISCLAIMER_TEXT}</DataDisclaimer>
      <TopLine>{zeroWidthNonBreakingSpace}</TopLine>
    </Root>
  );
}

const Root = styled.div`
  border-top: 1px solid ${BouColor.TOOL_BORDER_DARK_GRAY};
  ${bouToolCommonBoxPadding}
`;

const Text = styled.h5`
  ${getTitleStyle({ size: 'h5' })}
  ${fluidSpacing('margin-top', BouSpacingLevel.SMALL)};
  text-align: center;
  color: ${BouColor.TOOL_MODEL_VERSION_DISCLAIMER_GRAY};
`;

const FullDate = styled.p`
  ${getTextStyle({ size: 'xsmall' })}
  text-align: center;
  ${fluidSpacing('margin-top', BouSpacingLevel.TINY)};
  ${fluidSpacing('margin-bottom', BouSpacingLevel.TINYISH)};
  color: ${BouColor.TOOL_MODEL_VERSION_DISCLAIMER_GRAY};
`;

const TopLine = styled.div`
  width: 1px;
  background-color: ${BouColor.TOOL_BORDER_DARK_GRAY};
  margin-left: auto;
  margin-right: auto;
  ${fluidSpaceLg('height', 52)};
`;

const DataDisclaimer = styled.div`
  ${getTextStyle({ size: 'xxxxs' })}
  ${fluidSpacing('margin-bottom', BouSpacingLevel.SMALL)};
  max-width: 50em;
  text-align: justify;
  margin-left: auto;
  margin-right: auto;
  color: ${BouColor.TOOL_MODEL_VERSION_DISCLAIMER_GRAY};
  hyphens: auto;
`;

const SourceDisclaimer = styled(DataDisclaimer)`
  text-align: center;
  & {
    margin-bottom: 2em;
  }
`;
